import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "../../../components/Button";
import { ModalDelete } from "../../../components/ModalDelete";
import Icon from "../../../components/Icon";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import ModalCancelDocument from "../../Fiscal/components/ModalCancelDocument";
import ModalCorrectDocument from "../../Fiscal/components/ModalCorrectDocument";
import DocumentProblem from "../../Fiscal/components/DocumentProblem";
import { convertIntToMoney } from "../../../helpers/conversions";
import { renderError } from "../../../helpers/errors";
import api from "../../../services/api";
import { canUseGestao, getGestaoState, getToken, hasPermission } from "../../../services/auth";
import { GestaoTeaserInline } from "../../../components/GestaoTeaser";
import { getSubdomain } from "../../../helpers/domain";
import { PRESENCE_INDICATOR_OPTIONS, getModelLabel, getStatusLabel, isPending } from "../../Fiscal/helpers";
import { subscribeFiscalDocuments } from "../../Fiscal/realtime";

/**
 * Notas fiscais do pedido (BDM-887): emissão de NF-e/NFS-e pelo provedor
 * fiscal e o upload manual legado (para quem emite fora do sistema).
 *
 * Enquanto a nota está na fila ou processando, a tela se atualiza sozinha —
 * o usuário não precisa saber que a emissão é assíncrona.
 */
const OrderNfsTab = ({ order, onFixFields, onDocumentsChanged }) => {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [issuingModel, setIssuingModel] = useState(null);
    const [sendingId, setSendingId] = useState(null);

    const [documents, setDocuments] = useState([]);
    const [readiness, setReadiness] = useState({});
    const [configured, setConfigured] = useState(null);

    const [uploadedNfs, setUploadedNfs] = useState(order.nfs ?? []);
    const [draggingFiles, setDraggingFiles] = useState(false);
    const [nfToDelete, setNfToDelete] = useState(null);
    const [deletingNf, setDeletingNf] = useState(false);
    const fileInputRef = useRef(null);
    // Os modais de cancelar/corrigir são compartilhados com a lista de notas
    // (src/pages/Fiscal/components) — o ciclo de vida da nota tem que se
    // comportar igual nos dois lugares.
    const [documentToCancel, setDocumentToCancel] = useState(null);
    const [documentToCorrect, setDocumentToCorrect] = useState(null);
    const [resendingId, setResendingId] = useState(null);

    const [operations, setOperations] = useState([]);
    const [fiscalOperationId, setFiscalOperationId] = useState('');
    // Escolha do usuário não é sobrescrita pela natureza padrão que chega depois.
    const operationTouched = useRef(false);
    // Devolução (finalidade 4) referencia a NF-e original.
    const [referencedKeys, setReferencedKeys] = useState('');
    // Indicador de presença (B11b): como a venda aconteceu. O 9 é o neutro.
    const [presenceIndicator, setPresenceIndicator] = useState('9');

    const navigate = useNavigate();

    const hasFiscalFeature = canUseGestao();
    const canManageFiscal = hasPermission('manage_fiscal');
    const canViewFiscal = hasPermission(['view_fiscal', 'manage_fiscal']);

    useEffect(() => {
        setUploadedNfs(order.nfs ?? []);
    }, [order]);

    useEffect(() => {
        if (hasFiscalFeature && canViewFiscal) {
            getDocuments();
            getOperations();
        }
    }, [order?.id]);

    // Tempo real: só interessa a nota DESTE pedido — o canal é do tenant
    // inteiro, o filtro é aqui. Não há polling (decisão de 15/08): se o
    // aviso se perder, o card pendente tem o botão "Verificar agora".
    useEffect(() => {
        if (!hasFiscalFeature || !canViewFiscal || !order?.id) return undefined;

        const unsubscribe = subscribeFiscalDocuments(
            event => {
                if (String(event.order_id) === String(order.id)) {
                    getDocuments(true);
                }
            },
            () => getDocuments(true)
        );

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [order?.id]);

    const getOperations = () => {
        api.get(`/fiscal-operation`).then(res => {
            const resources = res.data.resources ?? [];

            setOperations(resources);
            setFiscalOperationId(current =>
                current || (resources.find(operation => operation.is_default) ?? resources[0])?.id || ''
            );
        }).catch(() => {});
    };

    const getDocuments = (silent = false) => {
        if (!silent) setLoading(true);

        api.get(`/fiscal-document/order/${order.id}`).then(res => {
            setDocuments(res.data.resources ?? []);
            setReadiness(res.data.readiness ?? {});
            setConfigured(!!res.data.configured);

            // A padrão da empresa que fatura o pedido, como na nota manual.
            if (res.data.default_fiscal_operation_id && !operationTouched.current) {
                setFiscalOperationId(res.data.default_fiscal_operation_id);
            }

            // A cadeia do pedido mostra a NF-e e vem de outra requisição.
            if (silent) onDocumentsChanged?.();
        }).catch(() => {}).then(() => setLoading(false));
    };

    const selectedOperation = operations.find(operation => operation.id === fiscalOperationId);
    const isReturnOperation = selectedOperation?.purpose === 'return';
    const parsedReferencedKeys = referencedKeys
        .split(/[\s,;]+/)
        .map(key => key.replace(/\D/g, ''))
        .filter(key => key.length === 44);

    const issue = model => {
        setIssuingModel(model);

        api.post(`/fiscal-document/order/${order.id}/issue`, {
            model,
            fiscal_operation_id: model === 'nfe' ? (fiscalOperationId || null) : null,
            referenced_keys: model === 'nfe' ? parsedReferencedKeys : [],
            presence_indicator: model === 'nfe' ? presenceIndicator : null,
        }).then(() => {
            toast.success('Nota gerada e pendente. Confira os dados e use "Emitir" para enviá-la ao fisco.');
            getDocuments(true);
        }).catch(renderError).then(() => setIssuingModel(null));
    };

    /** Envia ao fisco uma nota já gerada (pendente ou recusada). */
    const sendDocument = id => {
        setSendingId(id);

        api.post(`/fiscal-document/${id}/send`).then(() => {
            toast.success('Nota enviada. Assim que o fisco responder, o status aparece aqui.');
            getDocuments(true);
        }).catch(renderError).then(() => setSendingId(null));
    };

    const refreshDocument = id => {
        api.post(`/fiscal-document/${id}/refresh`)
            .then(() => getDocuments(true))
            .catch(renderError);
    };

    const resendEmail = id => {
        setResendingId(id);

        api.post(`/fiscal-document/${id}/resend-email`)
            .then(() => toast.success('E-mail reenviado ao cliente'))
            .catch(renderError)
            .then(() => setResendingId(null));
    };

    const afterLifecycleAction = message => {
        toast.success(message);
        getDocuments(true);
    };

    const openDocumentFile = (id, format) => {
        window.open(`${process.env.REACT_APP_URL}/fiscal-document/${id}/${format}?_token=${getToken()}&_subdomain=${getSubdomain()}`);
    };

    /** PDF/XML da carta de correção (rota própria, não é arquivo da nota). */
    const openCceFile = (id, format) => {
        window.open(`${process.env.REACT_APP_URL}/fiscal-document/${id}/cce/${format}?_token=${getToken()}&_subdomain=${getSubdomain()}`);
    };

    const uploadNfs = fileList => {
        const files = Array.from(fileList ?? []);
        const pdfs = files.filter(file => file.type === 'application/pdf' || /\.pdf$/i.test(file.name));

        if (pdfs.length < files.length) {
            toast.error('Só dá para anexar arquivos PDF. Os demais foram ignorados.');
        }

        if (pdfs.length === 0) return;

        setUploading(true);

        Promise.allSettled(pdfs.map(file => {
            const form = new FormData();
            form.append('order_id', order.id);
            form.append('file', file);

            return api.post(`/order/nf`, form, {
                headers: { "Content-Type": "multipart/form-data" }
            });
        })).then(results => {
            const stored = results.filter(result => result.status === 'fulfilled').map(result => result.value.data.resource);
            const failed = results.find(result => result.status === 'rejected');

            setUploadedNfs(current => [...current, ...stored]);

            if (stored.length > 0) {
                toast.success(stored.length === 1 ? 'Nota anexada' : `${stored.length} notas anexadas`);
            }

            if (failed) {
                renderError(failed.reason);
            }
        }).then(() => setUploading(false));
    };

    const pickNfFiles = () => fileInputRef.current?.click();

    const handleFileInput = event => {
        uploadNfs(event.target.files);
        event.target.value = '';
    };

    const isDraggingFiles = event => Array.from(event.dataTransfer?.types ?? []).includes('Files');

    const handleDragOver = event => {
        if (!isDraggingFiles(event)) return;

        event.preventDefault();
        setDraggingFiles(true);
    };

    const handleDragLeave = event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setDraggingFiles(false);
        }
    };

    const handleDrop = event => {
        if (!isDraggingFiles(event)) return;

        event.preventDefault();
        setDraggingFiles(false);
        uploadNfs(event.dataTransfer.files);
    };

    const deleteNf = () => {
        setDeletingNf(true);

        api.delete(`/order/nf/${nfToDelete.id}`).then(() => {
            setUploadedNfs(current => current.filter(nf => nf.id !== nfToDelete.id));
            setNfToDelete(null);
            toast.success('Nota removida');
        }).catch(renderError).then(() => setDeletingNf(false));
    };

    const copyAccessKey = key => {
        navigator.clipboard.writeText(key)
            .then(() => toast.success('Chave de acesso copiada'))
            .catch(() => toast.error('Não foi possível copiar a chave'));
    };


    const renderIssueCard = model => {
        const state = readiness[model] ?? {};
        const existing = documents.find(document =>
            document.model === model && ['authorized', 'queued', 'processing'].includes(document.status)
        );

        if (existing) return null;

        return (
            <div key={model} className={`fiscal-issue-card`}>
                <div className={`issue-head`}>
                    <strong>{getModelLabel(model)}</strong>
                    {state.can_issue
                        ? <span className={`issue-ready`}>Pronta para emitir</span>
                        : <span className={`issue-blocked`}>{(state.problems ?? []).length} pendência(s)</span>
                    }
                </div>

                {!state.can_issue && (state.problems ?? []).length > 0 &&
                    <ul className={`issue-problems`}>
                        {state.problems.map((problem, index) => (
                            <li key={index}>
                                {problem.message}
                                {problem.where === 'fiscal_settings' &&
                                    <button onClick={() => navigate('/fiscal/settings')}>Configurar</button>
                                }
                                {/* O dado que a nota usa é o do pedido, não o do
                                    cadastro: "Corrigir" leva à aba Detalhes e
                                    marca os campos furados. Marca os de todas
                                    as pendências, não só os desta linha, para
                                    resolver tudo numa passada. O cadastro fica
                                    como ação à parte, para o erro não voltar no
                                    próximo pedido. */}
                                {problem.fields?.length > 0 && onFixFields &&
                                    <button onClick={() => onFixFields(state.problems)}>Corrigir</button>
                                }
                                {problem.link &&
                                    <button onClick={() => navigate(problem.link)}>
                                        {problem.where === 'product' ? 'Abrir produto' : 'Abrir cadastro'}
                                    </button>
                                }
                            </li>
                        ))}
                    </ul>
                }

                {/* A natureza decide CFOP e tributação: a mesma mercadoria
                    sai diferente numa venda e numa remessa. */}
                {canManageFiscal && model === 'nfe' && operations.length > 0 &&
                    <div className={`issue-operation`}>
                        <InputSelect
                            label={`Natureza da operação`}
                            options={operations.map(operation => ({ label: operation.name, value: operation.id }))}
                            value={fiscalOperationId}
                            change={value => {
                                operationTouched.current = true;
                                setFiscalOperationId(value);
                            }}
                        />
                        <InputSelect
                            label={`Presença do comprador`}
                            options={PRESENCE_INDICATOR_OPTIONS}
                            value={presenceIndicator}
                            change={setPresenceIndicator}
                        />
                        {/* Devolução sem a chave da NF-e original é recusada
                            pela SEFAZ (rejeição 321). */}
                        {isReturnOperation &&
                            <Input
                                label={`Chave da NF-e original (44 dígitos) *`}
                                value={referencedKeys}
                                change={setReferencedKeys}
                            />
                        }
                    </div>
                }

                {canManageFiscal &&
                    <Button
                        type={state.can_issue ? `primary` : `secondary`}
                        size={`small`}
                        text={`Gerar ${model === 'nfe' ? 'NF-e' : 'NFS-e'}`}
                        action={() => issue(model)}
                        loading={issuingModel === model}
                        disabled={!state.can_issue || (model === 'nfe' && isReturnOperation && parsedReferencedKeys.length === 0)}
                    />
                }
            </div>
        );
    };

    const renderDocument = doc => {
        const problem = doc.friendly_problem;

        return (
            <div key={doc.id} className={`fiscal-document-card status-${doc.status}`}>
                <div className={`document-body`}>
                    <span className={`document-icon`}>
                        <Icon name={`receipt-text`} size={20} />
                    </span>

                    <div className={`document-info`}>
                        <div className={`document-head`}>
                            <div className={`document-title`}>
                                <strong>{getModelLabel(doc.model)}</strong>
                                {doc.number && <span className={`document-number`}>nº {doc.number}</span>}
                            </div>
                            <span className={`document-status status-${doc.status}`}>
                                {isPending(doc.status) && <Icon name={`loading`} className={`spin`} />}
                                {getStatusLabel(doc.status)}
                            </span>
                        </div>

                        <div className={`document-meta`}>
                            <strong className={`document-amount`}>{convertIntToMoney(doc.amount, true)}</strong>
                            {doc.authorized_at &&
                                <span>Autorizada em {moment(doc.authorized_at).format('DD/MM/YYYY [às] HH:mm')}</span>
                            }
                        </div>

                        {doc.access_key &&
                            <div className={`document-key`}>
                                <span className={`key-label`}>Chave de acesso</span>
                                <code>{doc.access_key.replace(/(\d{4})(?=\d)/g, '$1 ')}</code>
                                <button type={`button`} title={`Copiar chave`} onClick={() => copyAccessKey(doc.access_key)}>
                                    <Icon name={`copy`} size={14} />
                                </button>
                            </div>
                        }

                        {doc.status === 'draft' &&
                            <p className={`document-hint`}>
                                A nota está pronta, mas ainda não foi enviada ao fisco. Confira os dados e
                                clique em <strong>Emitir nota fiscal</strong> — até lá, nada foi transmitido.
                            </p>
                        }

                        {isPending(doc.status) &&
                            <p className={`document-hint`}>
                                Enviamos a nota ao fisco. A resposta costuma chegar em segundos — esta tela
                                atualiza sozinha.
                            </p>
                        }

                        <DocumentProblem problem={problem} />

                        {/* Carta de correção enviada tem que aparecer aqui: antes o
                            evento era gravado e nunca mostrado, e quem corrigia não
                            conseguia conferir nem o texto nem se o fisco aceitou. */}
                        {(doc.events ?? []).filter(event => event.type === 'correction').map(event => (
                            <div className={`document-correction ${event.status}`} key={event.id}>
                                <div className={`correction-head`}>
                                    <strong>Carta de correção {event.sequence ? `nº ${event.sequence}` : ''}</strong>
                                    <span>{{
                                        concluded: 'Registrada na SEFAZ',
                                        rejected: 'Recusada',
                                        processing: 'Processando',
                                    }[event.status] ?? event.status}</span>
                                </div>
                                <p>{event.content}</p>
                                {event.status === 'concluded' &&
                                    <button onClick={() => openCceFile(doc.id, 'pdf')}>
                                        Ver PDF da correção
                                    </button>
                                }
                            </div>
                        ))}
                    </div>
                </div>

                <div className={`document-actions`}>
                    {/* Nota gerada só sai daqui: emitir é ação explícita. */}
                    {doc.can_send && canManageFiscal &&
                        <Button
                            type={`primary`}
                            size={`small`}
                            svg={`send`}
                            text={doc.status === 'draft' ? `Emitir nota fiscal` : `Enviar de novo`}
                            action={() => sendDocument(doc.id)}
                            loading={sendingId === doc.id}
                        />
                    }
                    {doc.can_edit && canManageFiscal &&
                        <Button type={`transparent`} size={`small`} svg={`edit`} text={`Editar nota`} action={() => navigate(`/fiscal/documents/${doc.id}`)} />
                    }
                    {/* has_files/can_correct/can_cancel exigem, além do
                        status, que a nota exista no provedor — quem decide
                        isso é o backend. */}
                    {doc.has_files &&
                        <>
                            <Button type={`transparent`} size={`small`} svg={`download`} text={`PDF`} action={() => openDocumentFile(doc.id, 'pdf')} />
                            <Button type={`transparent`} size={`small`} svg={`download`} text={`XML`} action={() => openDocumentFile(doc.id, 'xml')} />
                            <Button
                                type={`transparent`}
                                size={`small`}
                                svg={`email`}
                                text={`Reenviar por e-mail`}
                                action={() => resendEmail(doc.id)}
                                loading={resendingId === doc.id}
                            />
                        </>
                    }
                    {canManageFiscal && doc.can_correct &&
                        <Button type={`transparent`} size={`small`} svg={`file-pen`} text={`Carta de correção`} action={() => setDocumentToCorrect(doc)} />
                    }
                    {['rejected', 'error'].includes(doc.status) && canManageFiscal &&
                        <Button
                            type={`transparent`}
                            size={`small`}
                            svg={`receipt-text`}
                            text={`Gerar de novo`}
                            action={() => issue(doc.model)}
                            loading={issuingModel === doc.model}
                        />
                    }
                    {/* Vale também para a carta de correção esperando o
                        fisco: a nota já está autorizada, mas o evento não. */}
                    {(isPending(doc.status) || doc.pending_event_at) &&
                        <Button type={`transparent`} size={`small`} svg={`refresh`} text={`Verificar agora`} action={() => refreshDocument(doc.id)} />
                    }
                    {canManageFiscal && doc.can_cancel &&
                        <Button type={`transparent`} size={`small`} btnClass={`action-cancel`} text={`Cancelar nota`} action={() => setDocumentToCancel(doc)} />
                    }
                </div>
            </div>
        );
    };

    return (
        <>
            <ModalDelete
                show={!!nfToDelete}
                action={deleteNf}
                loading={deletingNf}
                close={() => setNfToDelete(null)}
                text={`Remover esta nota anexada do pedido?`}
            />

            <ModalCancelDocument
                document={documentToCancel}
                close={() => setDocumentToCancel(null)}
                onDone={afterLifecycleAction}
            />

            <ModalCorrectDocument
                document={documentToCorrect}
                close={() => setDocumentToCorrect(null)}
                onDone={afterLifecycleAction}
            />

            <div className={`order-fiscal`}>
                {!hasFiscalFeature &&
                    <GestaoTeaserInline
                        state={getGestaoState()}
                        icon={`receipt-text`}
                        title={`Emissão de NF-e pelo pedido`}
                        text={`A nota sai daqui com os dados do pedido já preenchidos e fica guardada junto dele, com PDF e XML. Enquanto isso, anexe abaixo as notas emitidas em outro sistema.`}
                    />
                }

                {hasFiscalFeature && canViewFiscal &&
                    <section className={`order-fiscal-section`}>
                        <div className={`section-head`}>
                            <div>
                                <h3 className={`section-title`}>Emitidas pelo sistema</h3>
                                <p className={`section-hint`}>Geradas a partir deste pedido e transmitidas ao fisco.</p>
                            </div>
                        </div>

                        {configured === false &&
                            <div className={`fiscal-alert warning`}>
                                <Icon name={`alert`} />
                                <span>
                                    A emissão de notas ainda não está configurada.
                                    {canManageFiscal &&
                                        <button onClick={() => navigate('/fiscal/settings')}>Configurar agora</button>
                                    }
                                </span>
                            </div>
                        }

                        {/* Só nota de produto sai do pedido — a de serviço é
                            emitida manualmente em /fiscal/documents/new. */}
                        {configured &&
                            <div className={`fiscal-issue-cards`}>
                                {['nfe'].map(renderIssueCard)}
                            </div>
                        }

                        {loading && documents.length === 0 &&
                            <p className={`section-hint`}>Carregando notas...</p>
                        }

                        {documents.map(renderDocument)}
                    </section>
                }

                <section
                    className={`order-fiscal-section manual ${draggingFiles ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <input
                        ref={fileInputRef}
                        type={`file`}
                        accept={`application/pdf,.pdf`}
                        multiple
                        hidden
                        onChange={handleFileInput}
                    />

                    <div className={`section-head`}>
                        <div>
                            <h3 className={`section-title`}>Anexadas manualmente</h3>
                            <p className={`section-hint`}>
                                Notas emitidas em outro sistema, guardadas aqui só para consulta.
                            </p>
                        </div>
                        <Button
                            type={`secondary`}
                            size={`small`}
                            svg={`upload`}
                            text={uploading ? `Enviando...` : `Anexar nota`}
                            action={pickNfFiles}
                            disabled={uploading}
                        />
                    </div>

                    {uploadedNfs.length > 0
                        ? <>
                            <ul className={`manual-files`}>
                                {uploadedNfs.map(nf => (
                                    <li key={nf.id}>
                                        <span className={`file-icon`}>
                                            <Icon name={`pdf`} size={16} />
                                        </span>
                                        <div className={`file-info`}>
                                            <strong>Nota anexada</strong>
                                            <span>Enviada em {moment(nf.created_at).format('DD/MM/YYYY [às] HH:mm')}</span>
                                        </div>
                                        <Button
                                            type={`transparent`}
                                            size={`small`}
                                            svg={`external-link`}
                                            text={`Abrir`}
                                            action={() => window.open(nf.file_url)}
                                        />
                                        <button
                                            type={`button`}
                                            className={`file-remove`}
                                            title={`Remover nota`}
                                            aria-label={`Remover nota`}
                                            onClick={() => setNfToDelete(nf)}
                                        >
                                            <Icon name={`delete`} size={16} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            <p className={`manual-drop-hint`}>Arraste PDFs para cá para anexar mais notas.</p>
                        </>
                        : <button type={`button`} className={`manual-dropzone`} onClick={pickNfFiles} disabled={uploading}>
                            <Icon name={`upload`} size={20} />
                            <strong>{uploading ? 'Enviando...' : 'Arraste os PDFs das notas para cá'}</strong>
                            {!uploading && <span>ou clique para escolher no computador</span>}
                        </button>
                    }

                    {draggingFiles &&
                        <div className={`manual-drop-overlay`}>
                            <Icon name={`upload`} size={22} />
                            <strong>Solte para anexar</strong>
                        </div>
                    }
                </section>
            </div>
        </>
    );
};

export default OrderNfsTab;
